import { useRef, useState } from "react";
import { FileText, FileCode, FolderDown, FolderUp, Film, AlertTriangle, CheckCircle2, Square } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { toSRT, toVTT, download, parseSubtitles } from "@/lib/subtitles";
import { burnVideo } from "@/lib/burn";
import { resetWorker } from "@/lib/worker";

const baseName = (f) => (f ? f.name.replace(/\.[^.]+$/, "") : "captions");

const Row = ({ icon: Icon, title, desc, onClick, disabled, testid }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className="w-full flex items-center gap-3 p-3 rounded-lg bg-[var(--cc-panel)] border border-[var(--cc-border)] hover:border-[var(--cc-accent)] text-left disabled:opacity-40 disabled:hover:border-[var(--cc-border)] min-h-[60px]">
    <Icon className="h-5 w-5 text-[var(--cc-accent)] shrink-0" />
    <span><span className="font-semibold block text-sm">{title}</span><span className="text-xs text-[var(--cc-text-2)]">{desc}</span></span>
  </button>
);

const BurnSection = ({ file, captions, style, mobile }) => {
  const [st, setSt] = useState({ phase: "idle", pct: 0 });
  const ctrl = useRef(null);
  const run = async () => {
    ctrl.current = new AbortController();
    if (st.url) URL.revokeObjectURL(st.url);
    setSt({ phase: "running", pct: 0 });
    if (mobile) resetWorker("Model released to free memory for export");
    try {
      const r = await burnVideo({ file, captions, style, mobile, signal: ctrl.current.signal, onProgress: (pct) => setSt((s) => ({ ...s, pct })) });
      setSt({ phase: "done", pct: 1, url: URL.createObjectURL(r.blob), ...r });
      toast.success("Video exported and verified as playable");
    } catch (e) {
      setSt({ phase: "error", pct: 0, message: e.message, code: e.code });
    }
  };
  return (
    <div className="p-3 rounded-lg bg-[var(--cc-panel)] border border-[var(--cc-border)] space-y-3" data-testid="burn-section">
      <div className="flex items-start gap-3">
        <Film className="h-5 w-5 text-[var(--cc-accent)] shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-sm">Video with burned-in captions <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 ml-1">Experimental</span></p>
          <p className="text-xs text-[var(--cc-text-2)] mt-0.5">Renders your video + styled captions locally in real time (takes as long as the video). Keeps the original aspect ratio. Output is WebM (MP4 on Safari). Keep this tab visible while exporting. Heavy on phones.</p>
        </div>
      </div>
      {st.phase === "running" ? (
        <>
          <div className="h-2 rounded bg-[var(--cc-hi)] overflow-hidden"><div className="h-full bg-[var(--cc-accent)]" style={{ width: `${Math.round(st.pct * 100)}%` }} /></div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--cc-text-2)]" data-testid="burn-progress">Rendering… {Math.round(st.pct * 100)}%</span>
            <Button size="sm" variant="outline" onClick={() => ctrl.current.abort()} data-testid="burn-cancel-btn"><Square className="h-3.5 w-3.5 mr-1" /> Cancel</Button>
          </div>
        </>
      ) : (
        <Button onClick={run} disabled={!file || !captions.length} className="w-full h-11 font-bold" data-testid="burn-export-btn">Export video with captions</Button>
      )}
      {st.phase === "done" && (
        <div className="space-y-2" data-testid="burn-result">
          <p className="text-sm text-emerald-400 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Ready: {st.width}×{st.height}, {(st.blob.size / 1048576).toFixed(1)} MB ({st.ext.toUpperCase()})</p>
          <video src={st.url} controls playsInline className="w-full max-h-64 rounded bg-black" data-testid="burn-output-video" />
          <Button variant="secondary" className="w-full" onClick={() => download(st.blob, `${baseName(file)}-captioned.${st.ext}`)} data-testid="burn-download-btn">Download video</Button>
        </div>
      )}
      {st.phase === "error" && (
        <div role="alert" className="p-3 rounded-md bg-rose-500/10 border border-rose-500/40 text-sm text-rose-200 space-y-1" data-testid="burn-error">
          <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> {st.code === "CANCELLED" ? "Export cancelled" : "Video export failed"}</p>
          <p className="text-xs">{st.message}</p>
          <p className="text-xs text-rose-100/80">Fallback: download the SRT file above and import it into your editing app (CapCut, InShot, VN, Premiere, YouTube Studio).</p>
        </div>
      )}
    </div>
  );
};

export const ExportPanel = ({ file, captions, setCaptions, style, setStyle, mobile }) => {
  const projRef = useRef(null);
  const none = !captions.length;
  const name = baseName(file);
  const importProject = async (f) => {
    if (!f) return;
    try {
      const text = await f.text();
      if (/\.(srt|vtt)$/i.test(f.name)) {
        const caps = parseSubtitles(text);
        if (!caps.length) throw new Error("no captions");
        setCaptions(caps);
        return toast.success(`Imported ${caps.length} captions`);
      }
      const p = JSON.parse(text);
      if (!Array.isArray(p.captions)) throw new Error("invalid");
      setCaptions(p.captions);
      if (p.style) setStyle(p.style);
      toast.success(`Project loaded: ${p.captions.length} captions`);
    } catch (e) {
      toast.error("That file is not a valid CaptionCraft project, SRT or VTT.");
    }
  };
  return (
    <div className="space-y-4">
      <h2 className="text-xs font-semibold tracking-wider uppercase text-[var(--cc-text-2)]">Export</h2>
      {none && <p className="text-sm text-[var(--cc-text-2)]" data-testid="export-empty">Generate or import captions to enable exports.</p>}
      <div className="space-y-2">
        <Row icon={FileText} title="Download SRT" desc="Works with YouTube, Instagram editors, CapCut, Premiere, VLC" disabled={none} testid="export-srt-btn"
          onClick={() => { download(toSRT(captions), `${name}.srt`, "application/x-subrip;charset=utf-8"); toast.success("SRT downloaded"); }} />
        <Row icon={FileCode} title="Download VTT" desc="WebVTT for web players" disabled={none} testid="export-vtt-btn"
          onClick={() => download(toVTT(captions), `${name}.vtt`, "text/vtt;charset=utf-8")} />
        <Row icon={FolderDown} title="Save project file" desc="Captions + style (.json) to continue editing later" disabled={none} testid="export-project-btn"
          onClick={() => download(JSON.stringify({ app: "CaptionCraft AI", version: 1, captions, style }, null, 2), `${name}.captioncraft.json`, "application/json")} />
        <input ref={projRef} type="file" accept=".json,.srt,.vtt" className="hidden" data-testid="import-project-input" onChange={(e) => { importProject(e.target.files[0]); e.target.value = ""; }} />
        <Row icon={FolderUp} title="Open project / SRT / VTT" desc="Re-import to continue editing" onClick={() => projRef.current.click()} testid="import-project-btn" />
      </div>
      <BurnSection file={file} captions={captions} style={style} mobile={mobile} />
    </div>
  );
};
